import {
  ChangeDetectorRef,
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  signal,
} from '@angular/core';
import { ModalConfiguration } from 'src/app/models/ModalConfiguration';
import { CustomHttpClientService } from 'src/app/infrastructure/services/custom-http-client.service';
import { TimerService } from 'src/app/infrastructure/services/timer.service';
import { Observable, Subscription, interval } from 'rxjs';
import { takeWhile, tap } from 'rxjs/operators';
import { UserService } from 'src/app/services/user.service';
import { GeneralService } from 'src/app/services/general.service';
import { TextKeyConstants } from 'src/app/models/Constants';
import { UtilitiesService } from 'src/app/services/utilities.service';

@Component({
  selector: 'phone-verification-modal',
  templateUrl: './phone-verification-modal.component.html',
  styleUrls: ['./phone-verification-modal.component.scss'],
})
export class PhoneVerificationModalComponent
  implements OnChanges, OnDestroy, OnInit
{
  @Input() phoneNumber: string = '';
  @Input() isModalOpen: boolean = false;
  @Output() phoneHash = new EventEmitter<string>();
  @Output() close = new EventEmitter<void>();
  isConsentRequired = signal<boolean>(false);
  otpCode: string = '';
  isOtpVerified: boolean = false;
  showResendButton: boolean | undefined = false;
  resendTimeRemaining: string = '00:00';
  resendAttempt: number = 0;
  maxResendAttempts: number = 5;
  errorMessage: string = '';
  subscriptions: Subscription = new Subscription();
  resendTimeInSeconds: number = 60;
  username: string = '';
  resendLockOutTime: number = 10; // 10 minutes
  isMaxWrongAttempt: boolean = false;

  modalConfiguration: ModalConfiguration = {
    header: '',
    height: 750,
    width: 580,
    button2: {
      text: 'Verify OTP',
      buttonClass: 'button button-dark me-2 p-lr',
      callback: () => this.verifyOTP(),
      disableCallback: () =>
        this.otpCode.length !== 6 ||
        this.containsPenaltyTime() ||
        this.isMaxWrongAttempt,
    },
    includeCancelButton: false,
    cancelButtonText: 'Cancel',
    button1: {
      text: 'Cancel',
      buttonClass: 'button button-light me-2 p-lr',
      callback: () => {
        this.closeModal();
      },
      disableCallback: () => false,
    },
    isCloseable: true,
  };
  OtpInfo = '';
  enteredOTP: string = '';
  isConsentGiven = signal<boolean>(false);
  consentMessage: string = '';

  constructor(
    private httpClient: CustomHttpClientService,
    private readonly generalService: GeneralService,
    private cdr: ChangeDetectorRef,
    private userService: UserService,
    private timerService: TimerService,
    private utils: UtilitiesService,
  ) {
    this.subscriptions.add(
      this.generalService.textMessageArr.subscribe(() => {
        this.OtpInfo = this.generalService?.GetTextMessage(
          TextKeyConstants.OTPInfo,
        );
        this.consentMessage = this.generalService?.GetTextMessage(
          TextKeyConstants.SMS_Consent_Info,
        );
      }),
    );
  }

  public get sessionTimeRemaining$(): Observable<number> {
    return this.timerService.GetTimerObservable(this.username);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.isModalOpen = changes['isModalOpen'].currentValue;
    this.phoneNumber = changes['phoneNumber'].currentValue;
    if (!localStorage.getItem(this.getLastTimestampKey())) {
      this.isConsentGiven.set(false);
      this.resendTimeRemaining = '00:00';
      this.otpCode = '';
    }
    if (
      this.isModalOpen &&
      localStorage.getItem(this.getWrongOTPAttemptsKey())
    ) {
      this.restrictOTPAfterFiveWrongAttempt();
    }
    if (
      (this.isModalOpen &&
        localStorage.getItem(this.getLastTimestampKey()) &&
        localStorage.getItem(this.getResendAttemptsKey())) ||
      (this.isModalOpen &&
        !localStorage.getItem(this.getLastTimestampKey()) &&
        !localStorage.getItem(this.getResendAttemptsKey()) &&
        !localStorage.getItem(this.getWrongOTPAttemptsKey()))
    ) {
      setTimeout(() => {
        if (this.isResendTimerActive()) {
          return;
        }
      }, 1000);
    }
    if (changes['isModalOpen'].currentValue) {
      this.username = this.userService.getUserName() ?? 'userId';
    }
  }

  openModal(phone: string): void {
    this.phoneNumber = phone;
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isOtpVerified = false;
    this.isModalOpen = false;
    this.otpCode = '';
    this.modalConfiguration = {
      header: '',
      height: 750,
      width: 580,
      button2: {
        text: 'Verify OTP',
        buttonClass: 'button button-dark me-2 p-lr',
        callback: () => this.verifyOTP(),
        disableCallback: () => this.otpCode.length !== 6,
      },
      includeCancelButton: false,
      cancelButtonText: 'Cancel',
      button1: {
        text: 'Cancel',
        buttonClass: 'button button-light me-2 p-lr',
        callback: () => {
          this.closeModal();
        },
        disableCallback: () => false,
      },
      isCloseable: true,
    };
    this.close.emit();
  }

  ngOnInit() {
    this.subscriptions.add(
      this.utils.isConsentRequired.subscribe((value) => {
        this.isConsentRequired.set(value);
        if (this.isConsentRequired() && this.modalConfiguration.button2) {
          this.modalConfiguration.button2.disableCallback = () =>
            this.otpCode.length !== 6 ||
            this.containsPenaltyTime() ||
            this.isMaxWrongAttempt ||
            !this.isConsentGiven();
        }
      }),
    );
  }

  giveConsent(event: Event) {
    const val = (event.target as HTMLInputElement).checked;
    this.isConsentGiven.set(val);
    this.enteredOTP = this.isConsentGiven() ? this.enteredOTP : '';
    this.otpCode = '';
    if (this.modalConfiguration.button2) {
      this.modalConfiguration.button2.disableCallback = () =>
        this.otpCode.length !== 6 ||
        this.containsPenaltyTime() ||
        this.isMaxWrongAttempt ||
        !this.isConsentGiven();
    }
    if (val) {
      this.sendOTP();
    }
    this.cdr.detectChanges();
  }

  onOtpInput(event: any): void {
    event.target.value = event.target.value.replace(/[^0-9]/g, '');
    this.otpCode = event.target.value;
    this.cdr.detectChanges();
  }

  verifyOTP(): void {
    this.httpClient
      .post<any>(
        `/api/otp/verify?receiverId=${this.phoneNumber}&otp=${this.otpCode}`,
        {},
      )
      .subscribe({
        next: (response) => {
          if (response.errors && response.errors.length > 0) {
            this.handleErrors(response.errors);
          } else {
            this.phoneHash.emit(response.phoneHash);
            this.showSuccessMessage();
          }
        },
        error: (err) => {
          if (
            err.ErrorMessages.length &&
            err.ErrorMessages.includes('Max check attempts reached')
          ) {
            localStorage.removeItem(this.getLastTimestampKey());
            localStorage.removeItem(this.getResendAttemptsKey());
            localStorage.removeItem(this.getResendLockOutKey());
            if (
              (this.getWrongOTPAttemptsKey() &&
                this.getrestrictOTPAfterFiveWrongAttemptTimeDiff() > 600) ||
              !this.getWrongOTPAttemptsKey()
            ) {
              const errorDateTime = Math.floor(Date.now() / 1000).toString();
              const key = this.getWrongOTPAttemptsKey();
              localStorage.setItem(key, errorDateTime);
            }
            this.restrictOTPAfterFiveWrongAttempt();
          }
        },
      });
  }

  sendOTP(): void {
    if (this.isResendTimerActive()) {
      return;
    }
    if (this.manageResendAttempts()) {
      return;
    }

    this.httpClient
      .post<any>(`/api/otp/send?receiverId=${this.phoneNumber}`, {})
      .subscribe({
        next: () => {
          this.startResendTimer();
          this.cdr.detectChanges();
        },
        error: (err) => {
          if (
            err.ErrorCode === 'TwilioSendOTPException' ||
            (err.ErrorMessages.length &&
              (err.ErrorMessages[0].includes('Invalid parameter') ||
                err.ErrorMessages[0].includes('not a valid phone number')))
          ) {
            this.errorMessage = 'Something went wrong. Please try again later.';
            localStorage.removeItem(this.getResendAttemptsKey());
            this.cdr.detectChanges();
          }
          localStorage.removeItem(this.getWrongOTPAttemptsKey());
        },
      });
  }

  showSuccessMessage(): void {
    this.isOtpVerified = true;
    this.cdr.detectChanges();
    this.modalConfiguration.button2 = undefined;
    this.modalConfiguration.button1 = undefined;
    setTimeout(() => {
      this.closeModal();
      this.cdr.detectChanges();
    }, 2000);
    this.cdr.detectChanges();
  }

  handleErrors(errors: string[]): void {
    this.errorMessage = 'Incorrect OTP. Please try again.';
    this.cdr.detectChanges();
  }

  startResendTimer(): void {
    this.showResendButton = false;
    this.timerService.StartTimer(this.username, {
      durationInSeconds: this.getSecondsDifference(this.resendTimeInSeconds),
    });
    this.subscriptions.add(
      this.sessionTimeRemaining$.subscribe((time) => {
        if (time > 0) {
          this.resendTimeRemaining = time < 10 ? '0' + time : time.toString();
          this.cdr.detectChanges();
        }
        if (time === 0) {
          this.resendTimeRemaining = '00';
          this.showResendButton = true;
          this.cdr.detectChanges();
        }
      }),
    );
  }

  getLockOutTimeDiff(): number {
    const targetTimestamp = Number(
      localStorage.getItem(this.getResendLockOutKey()),
    );
    const currentTimestamp = Math.floor(Date.now() / 1000);
    return Math.abs(targetTimestamp - currentTimestamp);
  }

  manageResendAttempts(): boolean {
    this.errorMessage = '';
    const maxTime = 600;
    const penaltyTime = localStorage.getItem(this.getResendLockOutKey());
    const storedAttempts = localStorage.getItem(this.getResendAttemptsKey());
    const currentAttempts = storedAttempts ? parseInt(storedAttempts, 10) : 0;
    if (penaltyTime) {
      const currentTime = Math.floor(Date.now() / 1000);
      const penaltyTimeValue = parseInt(penaltyTime, 10);
      let differenceInSeconds = penaltyTimeValue - currentTime;
      if (differenceInSeconds > 0) {
        const subscription: Subscription = interval(1000)
          .pipe(
            tap(() => {
              differenceInSeconds--;
              const remainingSeconds = differenceInSeconds;
              const minutes = Math.floor(remainingSeconds / 60);
              const seconds = remainingSeconds % 60;
              const remainingTime = `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
              this.otpCode = '';
              this.errorMessage = `Maximum resend exceeded. Try again after ${remainingTime} minute(s).`;
              this.cdr.detectChanges();
              if (differenceInSeconds === 0) {
                this.errorMessage = '';
                this.showResendButton = true;
                this.sendOTP();
                subscription.unsubscribe();
                this.cdr.detectChanges();
              }
            }),
            takeWhile(() => differenceInSeconds < maxTime),
          )
          .subscribe();
        return true;
      } else {
        localStorage.setItem(this.getResendAttemptsKey(), '1');
        this.showResendButton = false;
        localStorage.removeItem(this.getResendLockOutKey());
        return false;
      }
    }
    const updatedAttempts = currentAttempts + 1;
    localStorage.setItem(
      this.getResendAttemptsKey(),
      updatedAttempts.toString(),
    );
    if (updatedAttempts > this.maxResendAttempts) {
      this.showResendButton = undefined;
      const newPenaltyTime =
        Math.floor(Date.now() / 1000) + this.resendLockOutTime * 60;
      localStorage.setItem(
        this.getResendLockOutKey(),
        newPenaltyTime.toString(),
      );
      let differenceInSeconds = this.getLockOutTimeDiff();
      const maxTime = 600;

      const subscription: Subscription = interval(1000)
        .pipe(
          tap(() => {
            differenceInSeconds++;
            const remainingSeconds = maxTime - differenceInSeconds;
            const minutes = Math.floor(remainingSeconds / 60);
            const seconds = remainingSeconds % 60;
            const remainingTime = `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
            this.otpCode = '';
            this.errorMessage = `Maximum resend exceeded. Try again after ${remainingTime} minute(s).`;
            this.cdr.detectChanges();
            if (differenceInSeconds >= maxTime) {
              this.errorMessage = '';
              this.showResendButton = true;
              this.sendOTP();
              subscription.unsubscribe();
              this.cdr.detectChanges();
            }
          }),
          takeWhile(() => differenceInSeconds < maxTime),
        )
        .subscribe();
      return true;
    }

    if (updatedAttempts === this.maxResendAttempts) {
      this.showResendButton = false;
      return false;
    }

    return false;
  }

  getrestrictOTPAfterFiveWrongAttemptTimeDiff(): number {
    const targetTimestamp = Number(
      localStorage.getItem(this.getWrongOTPAttemptsKey()),
    );
    const currentTimestamp = Math.floor(Date.now() / 1000);
    return Math.abs(targetTimestamp - currentTimestamp);
  }

  restrictOTPAfterFiveWrongAttempt() {
    let differenceInSeconds =
      this.getrestrictOTPAfterFiveWrongAttemptTimeDiff();
    const maxTime = 600;
    this.isMaxWrongAttempt = differenceInSeconds < maxTime;

    const subscription: Subscription = interval(1000)
      .pipe(
        tap(() => {
          differenceInSeconds++;
          const remainingSeconds = maxTime - differenceInSeconds;
          this.isMaxWrongAttempt = remainingSeconds > 0;
          const minutes = Math.floor(remainingSeconds / 60);
          const seconds = remainingSeconds % 60;
          const remainingTime = `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
          this.otpCode = '';
          if (this.enteredOTP) {
            this.enteredOTP = '';
          }
          this.errorMessage = `Maximum incorrect OTP attempts. Try again after ${remainingTime} minute(s).`;
          this.cdr.detectChanges();
          if (differenceInSeconds >= maxTime) {
            this.isMaxWrongAttempt = false;
            this.errorMessage = '';
            this.showResendButton = true;
            localStorage.removeItem(this.getWrongOTPAttemptsKey());
            subscription.unsubscribe();
            this.cdr.detectChanges();
          }
        }),
        takeWhile(() => differenceInSeconds < maxTime),
      )
      .subscribe();
  }

  containsPenaltyTime(): boolean {
    const penaltyTime = localStorage.getItem(this.getResendLockOutKey());
    return penaltyTime ? true : false;
  }

  storeCurrentTimestamp(): void {
    const currentTimestamp = Math.floor(Date.now() / 1000);
    localStorage.setItem(
      this.getLastTimestampKey(),
      currentTimestamp.toString(),
    );
  }

  getLastTimestampKey(): string {
    return this.phoneNumber + 'lastOtpTimestamp';
  }

  getResendAttemptsKey(): string {
    return this.phoneNumber + 'otpResendAttempts';
  }

  getResendLockOutKey(): string {
    return this.phoneNumber + 'resendLockOutTime';
  }

  getWrongOTPAttemptsKey(): string {
    return this.phoneNumber + 'wrongOTPAttemptTimeStamp';
  }

  getSecondsDifference(seconds: number): number {
    const storedTimestamp = localStorage.getItem(this.getLastTimestampKey());

    if (!storedTimestamp) {
      this.storeCurrentTimestamp();
      return seconds;
    }

    const nowTimestamp = Math.floor(Date.now() / 1000);
    const storedValue = parseInt(storedTimestamp, 10);
    const difference = nowTimestamp - storedValue;

    if (difference < seconds) {
      return seconds - difference;
    }

    this.storeCurrentTimestamp();
    return seconds;
  }

  isResendTimerActive(): boolean {
    const storageTime = localStorage.getItem(this.getLastTimestampKey());

    if (storageTime) {
      const nowTimestamp = Math.floor(Date.now() / 1000);
      const storedValue = parseInt(storageTime, 10);
      const difference = nowTimestamp - storedValue;

      if (difference > this.resendLockOutTime * 60) {
        localStorage.removeItem(this.getLastTimestampKey());
        localStorage.removeItem(this.getResendAttemptsKey());
        return false;
      }

      if (difference < this.resendTimeInSeconds) {
        this.startResendTimer();
        return true;
      }
    }

    return false;
  }

  ParsePhoneNumber() {
    let value = this.phoneNumber;
    if (this.phoneNumber.indexOf('-') === -1) {
      if (this.phoneNumber.length === 10) {
        value =
          this.phoneNumber.substring(0, 3) +
          '-' +
          this.phoneNumber.substring(3, 6) +
          '-' +
          this.phoneNumber.substring(6, 10);
      }
    }
    return value;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
